import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema, serviceSchema } from "@/data/site";
import { PageHero, Section, CtaBand } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { GoldButton, WhatsAppButtonLight } from "@/components/cta";
import { BirdMark } from "@/components/brand";
import { Info } from "lucide-react";

const features = [
  "A professional site that attracts and reassures parents",
  "Admissions enquiries and application forms",
  "Online fee payment (via Paystack / Mobile Money) or a link to your school system",
  "News, school calendar, photo gallery and prospectus download",
  "Ready for Ghana's Data Protection Act (Act 843) requirements for children's data",
];

export default function Schools() {
  return (
    <>
      <Seo
        title="School Website Design in Ghana | Admissions & Fee Payment — Sankofa Launch Studio"
        description="We build modern, mobile-first websites for private schools, nurseries, Montessori schools and training centres in Ghana. Attract parents, handle admissions enquiries, and accept online fee payment."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "For Schools", path: "/schools" }]),
          serviceSchema("School website design", "Mobile-first websites for Ghanaian schools with admissions enquiries and online fee payment."),
        ]}
      />
      <PageHero
        eyebrow="For Schools"
        breadcrumb="Home / For Schools"
        title="Professional websites for Ghanaian schools"
        intro="We build modern, mobile-first websites for private schools, nurseries, Montessori schools and training centres in Ghana. Our school websites attract parents, handle admissions enquiries, and can connect to online fee payment."
      />

      <Section className="bg-cream">
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-softlg">
              <img
                src="https://images.unsplash.com/photo-1627423896085-e3e694d88e40?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200&ixlib=rb-4.1.0"
                alt="Pupils raising their hands eagerly in a Ghanaian classroom"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-serif text-3xl font-600 text-navy md:text-4xl">What your school website includes</h2>
            </Reveal>
            <StaggerGroup className="mt-8 space-y-4">
              {features.map((f) => (
                <StaggerItem key={f}>
                  <div className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-soft">
                    <BirdMark className="mt-0.5 h-6 w-6 shrink-0" alt="" />
                    <p className="leading-relaxed text-ink">{f}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>

            <Reveal delay={0.1}>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 p-6">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <p className="text-sm leading-relaxed text-ink">
                  <strong className="text-navy">Need a parent portal for results and attendance?</strong> We connect your
                  website to a proven school management platform, or build a custom portal — ask us.
                </p>
              </div>
            </Reveal>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <GoldButton size="lg" testid="schools-book-btn">Book a free school briefing</GoldButton>
              <WhatsAppButtonLight size="lg" text="Hello Sankofa, I'd like a website for my school." testid="schools-whatsapp-btn" />
            </div>
          </div>
        </div>
      </Section>

      <CtaBand title="Let's build your school's website" waText="Hello Sankofa, I'd like a website for my school." />
    </>
  );
}
